import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { User, WalletAccount } from '../models/auth.models';
import { UserStateService } from './user-state.service';
import { WalletStateService } from './wallet-state.service';

@Injectable({
    providedIn: 'root'
})
export class WebStorageService {
    /**
     * These keys can remain in localStorage because they only support
     * wallet connector recovery and do not contain application auth data.
     */
    private readonly persistedWalletKeys = [
        'wagmi.store',
        'wagmi_new_store',
        'wagmi_connected',
        'wagmi.recentConnectorId'
    ];
    private readonly stateBackedKeys = new Set([
        'account',
        'regulated',
        'user',
        'has_new_item'
    ]);
    private readonly legacyAuthKeys = [
        'account',
        'regulated',
        'user',
        'has_new_item',
        'token',
        'auth_token',
        'authToken',
        'access_token',
        'refresh_token',
        'jwt',
        'jwt_token',
        'session',
        'session_token',
        'rw_token',
        'rw_auth_token',
        'rw_session',
        'authorization',
        'Authorization'
    ];
    private readonly authMigrationFlagKey = 'rw.auth.storage.migration';
    private readonly authMigrationVersion = '2';

    constructor(
        private readonly userState: UserStateService,
        private readonly walletState: WalletStateService
    ) {
        this.clearLegacyAuthBrowserStorage();
        this.runOneTimeAuthMigration();
    }

    setItem = (key: string, value: unknown) => {
        sessionStorage.setItem(key, this.serializeValue(value));
    }

    getItem = (key: string) => {
        return sessionStorage.getItem(key);
    }

    removeItem = (key: string) => {
        return sessionStorage.removeItem(key);
    }

    clearItem = () => {
        sessionStorage.clear();
        this.clearSensitiveState();
    }

    clearLocalStorage = () => {
        this.persistedWalletKeys.forEach((key) => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });
        this.clearLegacyAuthBrowserStorage();
        this.clearSensitiveState();
    }

    setLocalStorage = (key: string, value: unknown) => {
        if (this.isPersistedWalletKey(key)) {
            this.writeToLocalStorage(key, value);
            this.syncWalletState(key, value);
            return;
        }

        if (this.isStateBackedKey(key)) {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
            this.syncStateBackedValue(key, value);
            return;
        }

        this.writeToLocalStorage(key, value);
    }

    getLocalStorage = (key: string) => {
        if (this.isPersistedWalletKey(key)) {
            return this.readWalletState(key);
        }

        if (this.isStateBackedKey(key)) {
            return this.readStateBackedValue(key);
        }

        return localStorage.getItem(key);
    }

    removeLocalStorageItem = (key: string) => {
        if (this.isPersistedWalletKey(key)) {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
            this.syncWalletState(key, null);
            return;
        }

        if (this.isStateBackedKey(key)) {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
            this.syncStateBackedValue(key, null);
            return;
        }

        localStorage.removeItem(key);
    }

    private isPersistedWalletKey(key: string): boolean {
        return this.persistedWalletKeys.includes(key);
    }

    private isStateBackedKey(key: string): boolean {
        return this.stateBackedKeys.has(key);
    }

    private readWalletState(key: string): string | null {
        switch (key) {
            case 'account':
                return this.getStateValueOrHydrate(key, this.walletState.getWallet(), (value) => this.walletState.setWallet(value));
            case 'regulated':
                return this.getStateValueOrHydrate(key, this.walletState.getRegulated(), (value) => this.walletState.setRegulated(this.resolveRegulatedValue(value)));
            case 'wagmi.store':
            case 'wagmi_new_store':
                return this.getStateValueOrHydrate(key, this.walletState.getWagmiStore(), (value) => this.walletState.setWagmiStore(value));
            case 'wagmi_connected':
                return this.getStateValueOrHydrate(key, this.walletState.getWagmiConnected(), (value) => this.walletState.setWagmiConnected(value ?? false));
            case 'wagmi.recentConnectorId':
                return this.getStateValueOrHydrate(key, this.walletState.getRecentConnectorId(), (value) => this.walletState.setRecentConnectorId(value ?? null));
            default:
                return null;
        }
    }

    private syncWalletState(key: string, value: unknown): void {
        switch (key) {
            case 'account':
                this.walletState.setWallet(this.parseJson(value));
                break;
            case 'regulated':
                this.walletState.setRegulated(this.resolveRegulatedValue(value));
                break;
            case 'wagmi.store':
            case 'wagmi_new_store':
                this.walletState.setWagmiStore(this.parseJson(value));
                break;
            case 'wagmi_connected':
                this.walletState.setWagmiConnected(this.parseJson<boolean>(value) ?? false);
                break;
            case 'wagmi.recentConnectorId':
                this.walletState.setRecentConnectorId(this.parseJson<string>(value) ?? null);
                break;
            default:
                break;
        }
    }

    private readStateBackedValue(key: string): string | null {
        switch (key) {
            case 'account':
                return this.getStateValue(this.getEffectiveAccountState());
            case 'regulated':
                return this.serializeValue(this.walletState.getRegulated());
            case 'user':
                return this.getStateValue(this.userState.user);
            case 'has_new_item':
                return this.serializeValue(Boolean(this.userState.user?.has_new_item));
            default:
                return null;
        }
    }

    private syncStateBackedValue(key: string, value: unknown): void {
        switch (key) {
            case 'account':
                this.walletState.setWallet(this.parseJson(value));
                break;
            case 'regulated':
                if (value === null || value === undefined || value === 'null' || value === 'undefined') {
                    this.walletState.clearStoredRegulatedMode();
                    break;
                }

                this.walletState.setRegulated(this.resolveRegulatedValue(value));
                break;
            case 'user':
                this.userState.setUser(this.parseJson<User>(value));
                break;
            case 'has_new_item': {
                const currentUser = this.userState.user;
                if (!currentUser) {
                    break;
                }

                this.userState.setUser({
                    ...currentUser,
                    has_new_item: this.parseJson<boolean>(value) ?? false
                });
                break;
            }
            default:
                break;
        }
    }

    private clearSensitiveState(): void {
        this.walletState.clearStoredRegulatedMode();
        this.userState.clearAuthState();
    }

    private writeToLocalStorage(key: string, value: unknown): void {
        if (value === null || value === undefined) {
            localStorage.removeItem(key);
            return;
        }

        localStorage.setItem(key, this.serializeValue(value));
    }

    private serializeValue(value: unknown): string {
        return typeof value === 'string' ? value : JSON.stringify(value);
    }

    private getStateValue<T>(currentValue: T | null | undefined): string | null {
        if (currentValue === null || currentValue === undefined) {
            return null;
        }

        return this.serializeValue(currentValue);
    }

    private parseJson<T>(value: unknown): T | null {
        if (value === null || value === undefined || value === 'null' || value === 'undefined') {
            return null;
        }

        if (typeof value !== 'string') {
            return value as T;
        }

        try {
            return JSON.parse(String(value)) as T;
        } catch {
            return value as T;
        }
    }

    private getStateValueOrHydrate<T>(
        key: string,
        currentValue: T | null | undefined,
        hydrate: (value: T | null) => void
    ): string | null {
        /**
         * On normal navigation we prefer the already-hydrated in-memory state.
         * After a full page refresh that state is empty, so we rebuild it from
         * localStorage and return the persisted value to existing callers.
         */
        if (currentValue !== null && currentValue !== undefined) {
            return this.serializeValue(currentValue);
        }

        const storedValue = localStorage.getItem(key) ?? sessionStorage.getItem(key);
        if (!storedValue) {
            return null;
        }

        hydrate(this.parseJson<T>(storedValue));
        return storedValue;
    }

    private resolveRegulatedValue(value: unknown): boolean {
        const parsedValue = this.parseJson<boolean>(value);
        return typeof parsedValue === 'boolean' ? parsedValue : this.walletState.getRegulated();
    }

    private getEffectiveAccountState(): WalletAccount | null {
        const currentAccount = this.walletState.getWallet();
        if (this.hasWalletAddress(currentAccount)) {
            return currentAccount;
        }

        const derivedAccount = this.buildAccountFromUser();
        if (!derivedAccount) {
            return null;
        }

        this.walletState.setWallet(derivedAccount);
        return derivedAccount;
    }

    private buildAccountFromUser(): WalletAccount | null {
        const walletAddress = this.userState.user?.wallet_address;
        if (typeof walletAddress !== 'string' || walletAddress.trim().length === 0) {
            return null;
        }

        const currentAccount = this.walletState.getWallet();
        const isNonRegulatedWalletLogin = this.walletState.getRegulated() === false;
        const currentNetwork = this.walletState.getNetwork();
        const networkId = isNonRegulatedWalletLogin
            ? currentAccount?.networkId ?? currentNetwork?.networkId ?? Number(environment.DEFAULT_NETWORK)
            : Number(environment.DEFAULT_NETWORK);
        const walletProvider = isNonRegulatedWalletLogin
            ? this.normalizeWalletProvider(
                currentAccount?.walletProvider
                ?? currentNetwork?.walletProvider
                ?? this.walletState.getRecentConnectorId()
            )
            : '';

        return {
            walletAddress,
            chainId: currentAccount?.chainId ?? this.toHexChainId(networkId),
            networkId,
            walletProvider,
            init: false
        };
    }

    private hasWalletAddress(account: WalletAccount | null | undefined): account is WalletAccount {
        return typeof account?.walletAddress === 'string' && account.walletAddress.trim().length > 0;
    }

    private toHexChainId(networkId: number | string): string {
        const numericNetworkId = Number(networkId);
        if (!Number.isFinite(numericNetworkId) || numericNetworkId <= 0) {
            return environment.DEFAULT_CHAIN;
        }

        return `0x${numericNetworkId.toString(16)}`;
    }

    private normalizeWalletProvider(provider: string | null | undefined): string {
        if (provider === 'io.metamask') {
            return 'metamask';
        }

        if (provider === 'walletConnect') {
            return 'walletconnect';
        }

        return provider ?? '';
    }

    private clearLegacyAuthBrowserStorage(): void {
        this.legacyAuthKeys.forEach((key) => {
            this.safeRemove(localStorage, key);
            this.safeRemove(sessionStorage, key);
        });
    }

    /**
     * Returning users may carry pre-migration storage from older builds that
     * persisted the JWT or user object under names we no longer know about.
     * On first boot after a migration version bump, sweep both stores for any
     * key whose name hints at auth/session/token data. Wallet-connector keys
     * are preserved because they are needed for self-custody session recovery.
     */
    private runOneTimeAuthMigration(): void {
        try {
            if (localStorage.getItem(this.authMigrationFlagKey) === this.authMigrationVersion) {
                return;
            }

            this.sweepAuthLikeKeys(localStorage);
            this.sweepAuthLikeKeys(sessionStorage);

            localStorage.setItem(this.authMigrationFlagKey, this.authMigrationVersion);
        } catch {
            // Storage may be unavailable (private mode, blocked); leave the
            // in-memory state intact and skip the migration.
        }
    }

    private sweepAuthLikeKeys(store: Storage): void {
        const keysToRemove: string[] = [];

        for (let i = 0; i < store.length; i += 1) {
            const key = store.key(i);
            if (!key) {
                continue;
            }

            if (this.shouldPreserveDuringSweep(key)) {
                continue;
            }

            if (this.isAuthLikeKey(key)) {
                keysToRemove.push(key);
            }
        }

        keysToRemove.forEach((key) => this.safeRemove(store, key));
    }

    private shouldPreserveDuringSweep(key: string): boolean {
        if (this.isPersistedWalletKey(key) || key === this.authMigrationFlagKey) {
            return true;
        }

        // Reown/WalletConnect/Wagmi maintain their own session material under
        // these namespaces. Removing them would force a wallet reconnect.
        return [
            'wagmi.',
            'wagmi_',
            '@appkit',
            '@w3m',
            'w3m',
            'wc@',
            'WALLETCONNECT_',
            'WCM_',
            'W3M_'
        ].some((prefix) => key.startsWith(prefix));
    }

    private isAuthLikeKey(key: string): boolean {
        const normalized = key.toLowerCase();
        return [
            'token',
            'jwt',
            'auth',
            'session',
            'bearer',
            'authorization',
            'credential',
            'password',
            'secret'
        ].some((fragment) => normalized.includes(fragment));
    }

    private safeRemove(store: Storage, key: string): void {
        try {
            store.removeItem(key);
        } catch {
            // Ignore storage failures (private mode, quota, blocked).
        }
    }
}
